<template>
  <v-app
    id="app"
    dark
    class="overflow-x-auto"
  >
    <notifications/>
    <navigation class="flex-shrink-0 flex-grow-0" v-if="isNavigation"/>
    <v-main class="overflow-auto flex-grow-1 fill-height">
      <router-view></router-view>
    </v-main>
  </v-app>
</template>

<script>
  import { mapState } from 'vuex';
  import { defineAbilityFor } from '@/abilities';
  import { hasAnalyticsConsent } from '@/utils/analytics';

  import Navigation from "@/components/Navigation";
  const Notifications = () => import('@/components/Notifications');

  export default {
    name: 'App',

    metaInfo() {
      return {
        meta: [
          { property: 'og:locale', content: 'en_US' },
          { property: 'og:type', content: 'website' },
          { property: 'og:site_name', content: 'MaterialX Library' }
        ]
      }
    },

    components: {
      Notifications,
      Navigation,
    },

    data() {
      return {
        refreshTimeout: null,
        oneTrustInitAttempts: 0,
        oneTrustCheckInterval: 250, // ms
        oneTrustMaxWaitTime: 5000,  // ms
      };
    },

    computed: {
      ...mapState({
        user: state => state.account.user,
      }),

      isNavigation() {
        return !this.$route.meta.noNavigation;
      },

      maxOneTrustAttempts() {
        return Math.ceil(this.oneTrustMaxWaitTime / this.oneTrustCheckInterval);
      },
    },

    watch: {
      user: {
        immediate: true,
        handler(v, old) {
          this.defineAbility();
          if (!v) this.clearTimeoutRefresh();
          else if (!old) this.setTimeoutRefresh();
        },
      },

      isNavigation(v) {
        this.$root.$emit('show:navigation', v);
      },
    },

    created() {
      this.defineAbility();
      this.syncAnalyticsWithOneTrust();
      this.$bus.$on('logout', this.onLogout);
      window.addEventListener('focus', this.onAppFocus);
      window.addEventListener('blur', this.onAppBlur);
      
      // Listen for OneTrust consent changes
      const checkOneTrustReady = () => {
        this.oneTrustInitAttempts++;
        
        if (window.OneTrust && window.OptanonWrapper) {
          // Override OptanonWrapper to sync with Vue
          const originalWrapper = window.OptanonWrapper;
          window.OptanonWrapper = () => {
            originalWrapper();
            this.syncAnalyticsWithOneTrust(); // Re-sync when OneTrust updates
            // Notify components that OneTrust consent groups have been updated
            this.$root.$emit('onetrust:update');
          };
          
          if (process.env.VUE_APP_CONFIGURATION !== 'production') {
            console.log('OneTrust initialized successfully');
          }
        } else if (this.oneTrustInitAttempts < this.maxOneTrustAttempts) {
          setTimeout(checkOneTrustReady, this.oneTrustCheckInterval);
        } else {
          console.warn('OneTrust failed to initialize after', this.maxOneTrustAttempts, 'attempts');
        }
      };
      checkOneTrustReady();
    },

    beforeDestroy() {
      this.$bus.$off('logout', this.onLogout);
      window.removeEventListener('focus', this.onAppFocus);
      window.removeEventListener('blur', this.onAppBlur);
      this.clearTimeoutRefresh();
    },

    methods: {
      defineAbility() {
        this.$ability.update(defineAbilityFor(this.user).rules);
      },

      syncAnalyticsWithOneTrust() {
        // Sync Google Analytics with OneTrust consent
        if (hasAnalyticsConsent()) {
          this.$gtag.optIn();
          
          if (process.env.VUE_APP_CONFIGURATION !== 'production') {
            console.log('Google Analytics enabled via OneTrust consent');
          }
        } else {
          if (process.env.VUE_APP_CONFIGURATION !== 'production') {
            console.log('Google Analytics disabled - no OneTrust consent for C0002');
          }
        }
      },

      onAppBlur() {
        this.clearTimeoutRefresh();
      },

      onAppFocus() {
        if (!this.user) return;
        this.refreshTimeout = null;
        this.setTimeoutRefresh();
      },

      refreshToken() {
        this.$store.dispatch('account/refreshIfNotRefreshing');
      },

      setTimeoutRefresh() {
        if (this.refreshTimeout === -1) return;
        const threshold = this.$api.token.REFRESH_THRESHOLD;
        let expTimeout
        try {
          expTimeout = this.$api.token.getAccessTokenExpTimeout();
        } catch {
          return;
        }
        if (expTimeout === -1) return;
        if (expTimeout <= threshold) return this.$store.dispatch('account/refreshIfNotRefreshing').then(this.setTimeoutRefresh);
        let timeout = expTimeout - threshold;
        this.refreshTimeout = setTimeout(this.setTimeoutRefresh, timeout);
      },

      clearTimeoutRefresh() {
        if (!this.refreshTimeout) return;
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = -1;
      },

      onLogout() {
        if (this.$route.name !== 'main') this.$router.push({ name: 'main' });
      },
    },
  };
</script>
